import React from 'react';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { extractParams } from '@zapier/url-utils';
import { useCurrentAccountId } from '@zapier/payment/src/hooks/useCurrentAccountId';
import { useCurrencyRuleset } from '@zapier/payment/src/hooks/useCurrencyRuleset';
import { Currency, defaultCurrency } from '@zapier/currency';
import {
  ApolloInjection,
  ApiKeys,
  PaymentSourceType,
} from '@zapier/payment/src/types';
import usePaymentSourceState from './hooks/usePaymentSourceState';
import { PaymentMethodsSection } from './PaymentMethodsSection/PaymentMethodsSection';
import { Skeleton } from './Skeleton';
import { apiKeys as defaultApiKeys } from './apiKeys';

type Props = {
  accountId?: number;
  apiKeys?: ApiKeys;
  autoEdit?: boolean;
  editInModal?: boolean;
  onSaveSuccess?: () => void;
  selectedCurrency?: Currency;
} & ApolloInjection;

export function PaymentApp({
  accountId,
  apiKeys,
  autoEdit,
  editInModal,
  onSaveSuccess,
  selectedCurrency = defaultCurrency,
  ...apolloInjection
}: Props) {
  const { trackEvent } = useTrackingContext();
  const currentAccount = useCurrentAccountId(apolloInjection);

  const startEdit = Boolean(
    extractParams(window.location.href)?.section === PaymentSourceType.card ||
      autoEdit
  );

  const {
    currentSource,
    error,
    forceCardUpdate,
    isEditing,
    isLoading: isLoadingPaymentSource,
    isSaving,
    method,
    onCancelEdit,
    onEdit,
    onError,
    onHideSuccessMessage,
    onSave,
    onSelectMethod,
    showSuccessMessage,
    supportedMethods,
  } = usePaymentSourceState({
    accountId: accountId || currentAccount.accountId,
    onSaveSuccess,
    onTrack: trackEvent,
    startEdit,
    ...apolloInjection,
  });

  const {
    currencyRuleset,
    isLoading: isLoadingCurrencyRuleset,
  } = useCurrencyRuleset({
    accountId,
    ...apolloInjection,
  });
  const isLoading = isLoadingPaymentSource || isLoadingCurrencyRuleset;

  if (isLoading) {
    return <Skeleton />;
  }

  return (
    <PaymentMethodsSection
      apiKeys={{
        braintree: (apiKeys || defaultApiKeys).braintreePubKey,
        stripe: (apiKeys || defaultApiKeys).stripePubKey,
      }}
      currencyRuleset={currencyRuleset}
      currentMethod={method}
      editInModal={Boolean(editInModal)}
      error={error}
      forceCardUpdate={forceCardUpdate}
      hasPaymentSource={Boolean(currentSource)}
      isEditing={isEditing}
      isEditingInFlight={isSaving}
      onCancelEdit={onCancelEdit}
      onEdit={onEdit}
      onErrorEdit={onError}
      onHideSuccessMessage={onHideSuccessMessage}
      onSaveEdit={onSave}
      onSelectSource={onSelectMethod}
      savedSource={currentSource}
      selectedCurrency={selectedCurrency}
      showChangeSuccessMessage={showSuccessMessage}
      supportedMethods={supportedMethods}
    />
  );
}
